import { generateRoomWithoutSeparator } from '@jitsi/js-utils/random';
import { getTokenAuthUrl, isTokenAuthEnabled } from '../authentication/functions';
import { isRoomValid } from '../base/conference/functions';
import { isSupportedBrowser } from '../base/environment/environment';
import { MEDIA_TYPE } from '../base/media/constants';
import { toState } from '../base/redux/functions';
import { isLocalTrackMuted } from '../base/tracks/functions.any';
import { parseURIString } from '../base/util/uri';
import Conference from '../conference/components/web/Conference';
import { getDeepLinkingPage } from '../deep-linking/functions';
import { isWeChatMiniProgramWebView, leaveToWeChatMiniProgram } from '../portal/functions';
import PortalApp from '../portal/components/PortalApp';
import logger from '../portal/logger';
import UnsupportedDesktopBrowser from '../unsupported-browser/components/UnsupportedDesktopBrowser';
import BlankPage from '../welcome/components/BlankPage.web';
import { getCustomLandingPageURL, isWelcomePageEnabled } from '../welcome/functions';
/**
 * Determines which route is to be rendered in order to depict a specific Redux
 * store.
 *
 * @param {(Function|Object)} stateful - THe redux store, state, or
 * {@code getState} function.
 * @returns {Promise<Object>}
 */
export function _getRouteToRender(stateful) {
    const state = toState(stateful);
    return _getWebConferenceRoute(state) || _getWebWelcomePageRoute(state);
}
/**
 * 无 JWT 直开会议室时，强制跳到当前前端源的门户换票/登录页。
 * 必须用 window.location.origin，避免误跳到 Docker PUBLIC_URL（如 :8443）。
 * 注意：Jibri（iAmRecorder）不得走此跳转，见 _getWebConferenceRoute。
 *
 * @param {string} room - 会议室名。
 * @returns {string}
 */
function _buildSameOriginPortalAuthUrl(room) {
    const url = new URL(window.location.origin);
    url.searchParams.set('portalAuth', '1');
    url.searchParams.set('room', room);
    return url.toString();
}
/**
 * Returns the {@code Route} to display when trying to access a conference if
 * a valid conference is being joined.
 *
 * @param {Object} state - The redux state.
 * @returns {Promise|undefined}
 */
function _getWebConferenceRoute(state) {
    const room = state['features/base/conference'].room;
    if (!isRoomValid(room) || typeof room !== 'string') {
        return;
    }
    const roomName = room;
    const route = _getEmptyRoute();
    const jwt = state['features/base/jwt'].jwt;
    const { iAmRecorder, iAmSipGateway } = state['features/base/config'];
    // Jibri / SIP 网关无门户 JWT，靠 xmpp_*_override 入会；不可跳转 tokenAuthUrl，否则 FailedToJoinCall。
    const isMachineClient = Boolean(iAmRecorder || iAmSipGateway);
    // 未登录（无 JWT）且启用了 tokenAuth：进会议室前立刻跳门户登录，不渲染 Prejoin。
    // 机器入会（录制 bot）例外：继续渲染 Conference。
    // 微信小程序 web-view：禁止跳 H5 门户登录，回原生页。
    if (!jwt && isTokenAuthEnabled(state) && !isMachineClient) {
        if (isWeChatMiniProgramWebView()) {
            leaveToWeChatMiniProgram();
            logger.info(`微信小程序 web-view：无 JWT 不跳门户登录，回原生 | room=${roomName}`);
            route.component = BlankPage;
            return Promise.resolve(route);
        }
        const locationURL = state['features/base/connection'].locationURL;
        const locationHref = locationURL instanceof URL
            ? locationURL.href
            : window.location.href;
        const locationForAuth = locationURL instanceof URL
            ? locationURL
            : new URL(locationHref);
        const config = state['features/base/config'];
        const { tenant } = parseURIString(locationHref) || {};
        const audioMuted = isLocalTrackMuted(state['features/base/tracks'], MEDIA_TYPE.AUDIO);
        const videoMuted = isLocalTrackMuted(state['features/base/tracks'], MEDIA_TYPE.VIDEO);
        const { enabled: lowBandwidthModeEnabled } = state['features/base/low-bandwidth-mode'];
        return getTokenAuthUrl(config, locationForAuth, {
            audioMuted,
            lowBandwidthModeEnabled,
            skipPrejoin: true,
            videoMuted
        }, roomName, tenant).then((tokenAuthServiceUrl) => {
            // 无论 config 里写的是 8443 还是其它主机，网页端一律落到当前源门户。
            const target = _buildSameOriginPortalAuthUrl(roomName);
            logger.info(`无 JWT 直开会议室，跳转门户登录 | room=${roomName}`
                + ` configUrl=${tokenAuthServiceUrl || ''} target=${target}`);
            route.href = target;
            return route;
        });
    }
    if (!jwt && isTokenAuthEnabled(state) && isMachineClient) {
        logger.info(`机器入会跳过门户登录 | room=${roomName} iAmRecorder=${Boolean(iAmRecorder)}`
            + ` iAmSipGateway=${Boolean(iAmSipGateway)}`);
    }
    // Update the location if it doesn't match. This happens when a room is
    // joined from the welcome page. The reason for doing this instead of using
    // the history API is that we want to load the config.js which takes the
    // room into account.
    const { locationURL } = state['features/base/connection'];
    if (window.location.href !== locationURL?.href) {
        route.href = locationURL?.href;
        return Promise.resolve(route);
    }
    return getDeepLinkingPage(state)
        .then(deepLinkComponent => {
        if (deepLinkComponent) {
            route.component = deepLinkComponent;
        }
        else if (isSupportedBrowser()) {
            route.component = Conference;
        }
        else {
            route.component = UnsupportedDesktopBrowser;
        }
        return route;
    });
}
/**
 * Returns the {@code Route} to display when trying to access the welcome page.
 *
 * @param {Object} state - The redux state.
 * @returns {Promise<Object>}
 */
function _getWebWelcomePageRoute(state) {
    const route = _getEmptyRoute();
    // 挂断后 room 清空会走到 Welcome；小程序内禁止渲染 H5 门户登录页。
    if (isWeChatMiniProgramWebView()) {
        leaveToWeChatMiniProgram();
        logger.info('微信小程序 web-view：Welcome/门户路由改为 BlankPage 并回原生');
        route.component = BlankPage;
        return Promise.resolve(route);
    }
    if (isWelcomePageEnabled(state)) {
        if (isSupportedBrowser()) {
            const customLandingPage = getCustomLandingPageURL(state);
            if (customLandingPage) {
                route.href = customLandingPage;
            }
            else {
                // 使用会议门户（登录/首页/直链换票）替换默认 WelcomePage
                route.component = PortalApp;
            }
        }
        else {
            route.component = UnsupportedDesktopBrowser;
        }
    }
    else {
        // Web: if the welcome page is disabled, go directly to a random room.
        const url = new URL(window.location.href);
        url.pathname += generateRoomWithoutSeparator();
        route.href = url.href;
    }
    return Promise.resolve(route);
}
/**
 * Returns the default {@code Route}.
 *
 * @returns {Object}
 */
function _getEmptyRoute() {
    return {
        component: BlankPage,
        href: undefined
    };
}
